import { inject } from '@angular/core';
import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Resource } from 'app/shared/acl/acl.model';
import { AclService } from 'app/shared/acl/acl.service';
import { map, mergeMap, Observable, skipWhile, take } from 'rxjs';
import { AuthQuery } from './auth.query';
import { AuthService } from './auth.service';
import { agrees } from './user.model';


type Guard0 = () => Observable<UrlTree | boolean>;
type Guard2 = (_: unknown, state: RouterStateSnapshot) => Observable<UrlTree | boolean>;


export const authGuard: Guard2 = (_, state) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    return authService.isAuthenticated().pipe(
        skipWhile(loggedIn => typeof loggedIn === 'undefined'),
        take(1),
        map(loggedIn => loggedIn ? true : router.createUrlTree(['login'], { queryParams: { redirect: state.url } }))
    );
};


export const unAuthGuard: Guard0 = () => {
    const router = inject(Router);
    const authService = inject(AuthService);
    return authService.isAuthenticated().pipe(
        skipWhile(x => typeof x === 'undefined'),
        take(1),
        map(loggedIn => loggedIn ? router.createUrlTree(['/']) : true)
    );
};


export const isAdminGuard: Guard0 = () => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(uiData => uiData.isAdmin ? true : router.createUrlTree(['/']))
    );
};


export const hasHomeAreaGuard: Guard2 = (_, state) => {
    const router = inject(Router);
    const aclService = inject(AclService);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => aclService.allowed.pipe(map(_ => aclService.can(Resource.NO_HOME_AREA)))),
        take(1),
        map(can => can
            ? router.createUrlTree(['/my-home-area'], { queryParams: { redirect: state.url } })
            : true
        ),
    );
};


export const hasNoHomeAreaGuard: Guard0 = () => {
    const router = inject(Router);
    const aclService = inject(AclService);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => aclService.allowed.pipe(map(_ => aclService.can(Resource.NO_HOME_AREA)))),
        take(1),
        map(can => can
            ? true
            : router.createUrlTree(['/'])
        ),
    );
};


export const subscriptionValidGuard: Guard2 = (_, state) => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(data => data.subscriptionValid ? true : router.createUrlTree(['payment'], { queryParams: { redirect: state.url } }))
    );
};


export const subscriptionInvalidGuard: Guard0 = () => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(data => data.subscriptionValid ? router.createUrlTree(['/']) : true)
    );
};


export const hasProfessionGuard: Guard2 = (_, state) => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(uiData =>
            uiData.details.profession
                ? true
                : router.createUrlTree(['/my-profession'], { queryParams: { redirect: state.url } })
        )
    );
};


export const hasNoProfessionGuard: Guard0 = () => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(uiData => uiData.details.profession ? router.createUrlTree(['/']) : true)
    );
};


export const tosPpValidGuard: Guard2 = (_, state) => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(user =>
            agrees(user, 'pp') && agrees(user, 'tos')
                ? true
                : router.createUrlTree(['terms-of-services'], { queryParams: { redirect: state.url } })
        )
    );
};


export const tosPpInvalidGuard: Guard0 = () => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(user => !agrees(user, 'pp') || !agrees(user, 'tos') ? true : router.createUrlTree(['/']))
    );
};


export const shouldSeeBoardLandingGuard: Guard2 = (_, state) => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(user =>
            !user.details.showBoardLandingPage ? true : router.createUrlTree(['welcome'], { queryParams: { redirect: state.url } })
        )
    );
};


export const isDisallowedToSeeBoardLandingGuard: Guard0 = () => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(user => user.details.showBoardLandingPage ? true : router.createUrlTree(['/']))
    );
};


export const hasReloginRequiredGuard: Guard0 = () => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(user => user.needsSsoRelogin ? router.createUrlTree(['relogin']) : true)
    );
};


export const hasNoReloginRequiredGuard: Guard0 = () => {
    const router = inject(Router);
    const authQuery = inject(AuthQuery);
    return authQuery.whenUserInfoLoaded$().pipe(
        mergeMap(() => authQuery.getUserInfo$()),
        take(1),
        map(user => user.needsSsoRelogin ? true : router.createUrlTree(['/']))
    );
};
